import { FC, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Popover } from 'antd'
import clsx from 'clsx'
import { StringParam, useQueryParam } from 'use-query-params'
import { CSSTransition } from 'react-transition-group'
import { Sidebar, Menu, Bell, Search } from 'react-feather'
import get from 'lodash.get'
import { useRouteMatch } from 'react-router-dom'

import {
  Modal,
  useAuthState,
  useGlobalState,
  useShowMessage,
  useUserDetails,
  useUpdateUserDetails,
  useGetNotificationCount,
} from '@pepper/ui'
import { joinPendingInvites } from '@pepper/utils/src/api/invites/invites'

import { OrgModalType } from 'container/Dashboard/Dashboard'
import SideExpanded from 'components/SideModal/SideExpanded/SideModalDetails'
import { useSelectedCompanyId } from 'utils/functions/misc'
import Notifications from 'components/Notifications/Notifications'

import UniversalSearch from './UniversalSearch/UniversalSearch'
import UserDropdownContainer from './UserDropdown'
import s from './Topbar.module.css'

type TopbarProps = {
  collapse: boolean
  toggleMenu: () => void
  toggleMobileMenu?: () => void
  joinOrganisationModal?: OrgModalType
  setJoinOrganisationModal?: (args: OrgModalType) => void
}

const Topbar: FC<TopbarProps> = ({
  toggleMenu,
  toggleMobileMenu,
  collapse,
  joinOrganisationModal,
  setJoinOrganisationModal,
}: TopbarProps) => {
  const [showNotification, setShowNotification] = useState(false)
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const companyId = useSelectedCompanyId()
  const { user } = useAuthState()
  const message = useShowMessage()
  const { mutateAsync: editDetails } = useUpdateUserDetails()
  const { refetchUserDetails } = useUserDetails(user.uid)
  const notificationCount = useGetNotificationCount({ data: { companyId } })
  const queryClient = useQueryClient()
  const isContentStudioMatch = useRouteMatch('/content-studio/app')
  const isContentStudioChatMatch = useRouteMatch('/content-studio/chat')

  const { viewport, topbarComponent, assignmentRefetchFunction } =
    useGlobalState()
  const [viewAssignmentId, setViewAssignmentId] = useQueryParam(
    'view_assignment',
    StringParam
  )

  const isMobileView = ['sm', 'md'].includes(viewport)

  const handleOnConfirmJoinOrganisation = async () => {
    if (!joinOrganisationModal || joinOrganisationModal.data === null) return

    try {
      const result = await joinPendingInvites({
        inviteId: joinOrganisationModal.data.invite_id,
      })

      if (result.status === 'ok') {
        message({ type: 'success', message: 'Joined successfully.' })
        setJoinOrganisationModal?.({ visible: false, data: null })
        refetchUserDetails()

        window.location.href = `/${joinOrganisationModal.data.id}`
      } else {
        message({ type: 'error', message: result.error })
      }
    } catch (error) {
      message({ type: 'error', message: error.message })
    }
  }

  const markAllAsSeen = async () => {
    await editDetails({
      id: user.uid,
      body: {
        dashboard_notification_seen: true,
      },
    })
    queryClient.invalidateQueries(['notifications', 'count'])
  }

  const companyName = get(joinOrganisationModal, 'data.company_name', '').trim()

  if (isContentStudioMatch || isContentStudioChatMatch) return null
  return (
    <>
      <div
        className={clsx(s.topbar, {
          [s.isMobile]: isMobileView,
        })}
      >
        <div className={s.left}>
          {isMobileView ? (
            <div
              role="none"
              onClick={toggleMobileMenu}
              data-id="business-topbar-hamburger-button"
            >
              <Menu size={18} className={s.icon} />
            </div>
          ) : (
            <div
              role="none"
              onClick={toggleMenu}
              data-id="business-topbar-hamburger-button"
              title={collapse ? 'Expand Menu' : 'Collapse Menu'}
            >
              <Sidebar size={18} className={s.icon} />
            </div>
          )}
          {topbarComponent}
        </div>
        <div className={s.right}>
          {isMobileView ? (
            <button
              className={clsx(s.iconContainer, {
                [s.active]: showMobileSearch,
              })}
              onClick={() => setShowMobileSearch(!showMobileSearch)}
            >
              <Search size={16} />
            </button>
          ) : (
            <UniversalSearch isMobileView={isMobileView} />
          )}
          <Popover
            content={
              <Notifications
                isMobileView={isMobileView}
                unseenCount={notificationCount.data?.unseen ?? 0}
                onClose={() => setShowNotification(false)}
              />
            }
            trigger="click"
            autoAdjustOverflow={true}
            visible={showNotification}
            onVisibleChange={visible => setShowNotification(visible)}
            getPopupContainer={_node =>
              document.getElementById('notification-popover-container') ??
              document.body
            }
            overlayStyle={{
              padding: '0',
              width: '400px',
              overflow: 'hidden',
              border: '1px solid #E9E2EC',
              borderRadius: '8px',
              zIndex: 99999,
            }}
          >
            <button
              className={clsx(s.iconContainer, {
                [s.active]: showNotification,
                [s.highlight]: !!notificationCount.data?.unseen,
              })}
              onClick={() => {
                markAllAsSeen()
                setShowNotification(true)
              }}
            >
              <Bell size={16} />
            </button>
          </Popover>

          <UserDropdownContainer
            setJoinOrganisationModal={setJoinOrganisationModal}
          />
        </div>
      </div>
      <CSSTransition
        in={isMobileView && showMobileSearch}
        timeout={300}
        classNames={{
          enter: s.openSearch,
          enterActive: s.openSearchActive,
          exit: s.closeSearch,
          exitActive: s.closeSearchActive,
        }}
        unmountOnExit={true}
      >
        <div className={s.searchContainer}>
          <UniversalSearch isMobileView={isMobileView} />
        </div>
      </CSSTransition>
      {viewAssignmentId && (
        <>
          <div
            className={s.sideViewDetailsBackDrop}
            onClick={() => {
              setViewAssignmentId(undefined)
            }}
            aria-hidden="true"
          />
          <div className={s.sideViewDetails}>
            {assignmentRefetchFunction && (
              <SideExpanded
                hideSideLoad={() => {
                  assignmentRefetchFunction()
                  setViewAssignmentId(undefined)
                }}
                assignmentDetails={viewAssignmentId}
                refetch={assignmentRefetchFunction}
              />
            )}
          </div>
        </>
      )}
      {joinOrganisationModal?.visible && (
        <Modal
          centered={true}
          title={`Joining ${companyName}?`}
          visible={true}
          onCancel={() => {
            setJoinOrganisationModal?.({ visible: false, data: null })
          }}
          okText="Confirm"
          onOk={handleOnConfirmJoinOrganisation}
        >
          <p>
            Are you sure you want to join{' '}
            {`${joinOrganisationModal.data?.company_name}`.trim()}?
          </p>
        </Modal>
      )}
    </>
  )
}

export default Topbar
